/**
 * useExportActions — resume download logic (PDF, JSON, PNG).
 *
 * Extend: add PNG export via html2canvas, share-link generation, etc.
 */
import { t } from "@lingui/macro";
import { useCallback, useRef, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume/print";
import { useResumeStore } from "@/client/stores/resume";
import { openPlaceholderTab } from "@/client/utils/open-after-await";

import type { ExportFormat } from "../modals/export-modal";

export type ExportStatus = "idle" | "generating" | "ready";

export const useExportActions = () => {
  const resume = useResumeStore((s) => s.resume);
  const { printResume: printResumeFn } = usePrintResume();
  const [exportStatus, setExportStatus] = useState<ExportStatus>("idle");
  const generating = useRef(false);

  const downloadPdf = useCallback(
    async (variant?: "ats") => {
      if (generating.current) return;
      generating.current = true;
      const tab = openPlaceholderTab();
      setExportStatus("generating");
      try {
        const result = await printResumeFn({ id: resume.id, ...(variant ? { variant } : {}) });
        if (!result.url) throw new Error("PDF URL missing");
        tab.navigate(result.url);
        setExportStatus("ready");
      } catch {
        tab.close();
        setExportStatus("idle");
        toast({
          variant: "error",
          title: t`Couldn't export the resume`,
          description: t`Please retry in a moment.`,
        });
      } finally {
        generating.current = false;
      }
    },
    [printResumeFn, resume.id],
  );

  const handleDownload = useCallback(
    async (format: ExportFormat) => {
      if (format === "json") {
        const blob = new Blob([JSON.stringify(resume.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${resume.title || "resume"}.json`; // eslint-disable-line lingui/no-unlocalized-strings
        document.body.append(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setExportStatus("ready");
        return;
      }

      if (format === "pdf") {
        return downloadPdf();
      }

      // PNG — placeholder for future html2canvas integration
      toast({
        variant: "info",
        title: t`Coming soon`,
        description: t`PNG export will be available in a future update.`,
      });
    },
    [resume, downloadPdf],
  );

  // Spec 046 — ATS export: a clean single-column, text-selectable PDF (server-side variant).
  const handleDownloadAts = useCallback(() => downloadPdf("ats"), [downloadPdf]);

  const resetStatus = useCallback(() => {
    setExportStatus("idle");
  }, []);

  return { exportStatus, handleDownload, handleDownloadAts, resetStatus };
};
